import config from 'config/config';
import i18next from 'i18next';
import ChainedBackend, { ChainedBackendOptions } from 'i18next-chained-backend';
import HttpBackend, {
  HttpBackendOptions,
  RequestCallback,
} from 'i18next-http-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import 'intl-pluralrules';
import { initReactI18next } from 'react-i18next';
import { preferredLanguage } from 'states/persistInStorage';
import { Lang } from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';

import {
  fallbackLanguage,
  getDeviceLanguage,
  supportedLanguages,
} from 'utils/languageUtils';
import en from './localizations/en.json';
import fi from './localizations/fi.json';
import no from './localizations/no.json';
import sv from './localizations/sv.json';

type ResourceType = {
  [key: string]: { translation: any };
};

const resources: ResourceType = {
  en: {
    translation: en,
  },
  sv: {
    translation: sv,
  },
  fi: {
    translation: fi,
  },
  no: {
    translation: no,
  },
};

const baseURL = (config.VITE_HTTP_BACKEND_TRANSLATIONS_URL as string).replace(
  '.lng',
  '{{lng}}'
);
const allowedLanguages = ['en', 'sv', 'fi'] as Lang[];

i18next
  .use(ChainedBackend)
  .use(initReactI18next)
  .init({
    debug: false,
    react: { useSuspense: isWeb },
    fallbackLng: fallbackLanguage,
    supportedLngs: supportedLanguages,
    partialBundledLanguages: true,
    // Lokalise exports untranslated keys as "" — treat them as missing so fallbackLng applies.
    returnEmptyString: false,
    interpolation: {
      escapeValue: false, // not needed for react as it does escape per default to prevent xss!
    },
    // Try to look for the missing key from the local JSON files.
    parseMissingKeyHandler: (key) => {
      try {
        return (
          key
            .split('.')
            .reduce((a, b) => a[b], resources[getLanguage()].translation) || key
        );
      } catch {
        return key;
      }
    },
    backend: {
      backends: [HttpBackend, resourcesToBackend(resources)],
      backendOptions: [
        {
          loadPath: `${baseURL}.json`,
          addPath: baseURL,
          request: (
            _: HttpBackendOptions,
            url: string,
            _2: any,
            callback: RequestCallback
          ) => {
            const lastUrlElement = url.split('/').pop();
            const fetchUrl = allowedLanguages.some((lng) =>
              lastUrlElement?.includes(lng)
            )
              ? url
              : _.loadPath?.toString().replace('{{lng}}', fallbackLanguage);
            fetch(fetchUrl ?? url, {
              headers: {
                pragma: 'no-cache',
                'cache-control': 'no-cache',
              },
            })
              .then((res) => res.json())
              .catch((e) => callback(e, { status: 500 } as any))
              .then((data) => {
                callback(null, {
                  data,
                  status: 200,
                });
              });
          },
        } as HttpBackendOptions,
        null,
      ],
    } as ChainedBackendOptions,
    initImmediate: false,
  });

const setupLanguages = (showLangNO: boolean) => {
  if (showLangNO && allowedLanguages.indexOf('no') === -1) {
    allowedLanguages.push('no');
  }
  setLanguage(preferredLanguage() ?? (getDeviceLanguage() as Lang));
};

const getLanguage = () => {
  return i18next.language;
};

const setLanguage = (lng: Lang) => {
  preferredLanguage(lng);
  i18next.changeLanguage(lng);
};

const t = i18next.t.bind(i18next);

export {
  allowedLanguages,
  getLanguage,
  i18next,
  setLanguage,
  setupLanguages,
  t,
};
